/**
 * The help pages, read straight from docs/customer-docs at build time.
 *
 * Not a copy: that directory is the source writers edit and CI link-checks,
 * and a second copy inside src is a second version of the truth. Vite inlines
 * the files into the bundle, so nothing is deployed separately and nothing is
 * fetched at runtime.
 *
 * A glob that matches nothing is an empty object rather than an error, which
 * would ship a help centre with no pages in it and a green build. The
 * help-pages-exist plugin in vite.config.js fails the build on that.
 */
const RAW = import.meta.glob('../../../docs/customer-docs/**/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
});

const AUDIENCE_ORDER = ['customer', 'technician', 'operator'];

export const AUDIENCES = {
  customer: { label: 'Using the portal', blurb: 'Tickets, invoices, quotes and your account' },
  technician: { label: 'For engineers', blurb: 'Working tickets from chat, and logging time' },
  operator: { label: 'Administration', blurb: 'Setting the portal up and running it' },
};

/**
 * Enough YAML for the front matter these pages use: scalars, and lists written
 * as `- ` items. The same subset the docs' own index builder parses, kept in
 * step deliberately — a page that indexes cannot fail to render, and the other
 * way round.
 */
function frontMatter(text) {
  const m = text.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?/);
  if (!m) return [{}, text];
  const meta = {};
  let key = null;
  for (const line of m[1].split(/\r?\n/)) {
    const item = line.match(/^\s*-\s+(.*)$/);
    if (item && key) {
      meta[key].push(unquote(item[1]));
      continue;
    }
    const kv = line.match(/^([A-Za-z_]+):\s*(.*)$/);
    if (!kv) continue;
    key = kv[1];
    const value = kv[2].trim();
    if (value === '') meta[key] = [];
    else if (value.startsWith('[')) meta[key] = value.slice(1, -1).split(',').map(unquote).filter(Boolean);
    else meta[key] = unquote(value);
  }
  return [meta, text.slice(m[0].length)];
}

const unquote = (s) => s.trim().replace(/^["']|["']$/g, '').trim();

// README, INVENTORY and TROUBLESHOOTING are for whoever writes the docs, not
// for whoever reads them. TROUBLESHOOTING in particular is generated, and the
// portal builds its own index from the same front matter.
const INTERNAL = /\/(README|INVENTORY|TROUBLESHOOTING)\.md$/;

export const PAGES = Object.entries(RAW)
  .filter(([path]) => !INTERNAL.test(path))
  .map(([path, text]) => {
    const [meta, body] = frontMatter(text);
    const slug = path
      .replace(/^.*\/docs\/customer-docs\//, '')
      .replace(/\.md$/, '');
    return {
      slug,
      title: meta.title || slug,
      audience: meta.audience || 'operator',
      // `flag: null` in the front matter means always available.
      flag: meta.flag && meta.flag !== 'null' ? meta.flag : null,
      symptoms: Array.isArray(meta.symptoms) ? meta.symptoms : [],
      body,
    };
  })
  .filter((p) => p.title)
  .sort((a, b) => {
    const byAudience = AUDIENCE_ORDER.indexOf(a.audience) - AUDIENCE_ORDER.indexOf(b.audience);
    return byAudience || a.title.localeCompare(b.title);
  });

export const bySlug = (slug) => PAGES.find((p) => p.slug === slug) || null;

/** Every symptom, flattened, for the search box and the troubleshooting list. */
export const SYMPTOMS = PAGES.flatMap((p) => p.symptoms.map((symptom) => ({ symptom, page: p })));

/**
 * Which flags the visible pages actually reference, so the page can ask for
 * exactly those rather than hard-coding a list that drifts as modules are
 * added.
 */
export const REFERENCED_FLAGS = [...new Set(PAGES.map((p) => p.flag).filter(Boolean))];
